import store from '@state/store'

// auth related routes
const authRoutes = [
{
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/pages/account/login')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/forget-password',
    name: 'forget-password',
    component: () =>
      lazyLoadView(import('@views/pages/account/forgetPassword'))
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () =>
      lazyLoadView(import('@views/pages/account/passwordReset')),
    },
  {
    path: '/logout',
    name: 'logout',
    meta: 
    {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) 
      {
        localStorage.removeItem('auth.currentUser');

        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(
          authRequiredOnPreviousRoute ? { name: 'dashboard' } : { ...routeFrom }
        )
      },
    },
  },
]

// error pages
const errorPagesRoutes = [
  {
    path: '/404',
    name: '404',
    component: require('@views/pages/secondary/error-404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  {
    path: '/500',
    name: '500',
    component: require('@views/pages/secondary/error-500').default,
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// dashboard
const dashboardRoutes = [
  {
    path: '/',
    name: 'Dashboard',
    header: 'Navigation',
    icon: 'home',
    sidebar:true,
    component: () => lazyLoadView(import('@views/pages/dashboard/dashboard')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]



const settingsRoutes = [
  {
    path: '/settings',
    name: 'Settings',
    icon: 'file',
    sidebar:false,
    component: () => lazyLoadView(import('@views/pages/settings')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  }
];


const creativesRoutes = [
  {
    path: '/creatives',
    name: 'Creative Center',
    icon: 'image',
    sidebar:true,
    component: () => lazyLoadView(import('@views/pages/creatives/list')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/creatives/edit/:id',
    component: () => lazyLoadView(import('@views/pages/creatives/edit')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
];


const hiddenRoutes = 
[
  {
    path: '/users/new',
    component: () => lazyLoadView(import('@views/pages/users/new')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/users',
    component: () => lazyLoadView(import('@views/pages/users/list')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/users/edit/:id',
    component: () => lazyLoadView(import('@views/pages/users/edit')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/advertisers/new',
    component: () => lazyLoadView(import('@views/pages/advertisers/new')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/advertisers',
    component: () => lazyLoadView(import('@views/pages/advertisers/list')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/advertisers/edit/:id',
    component: () => lazyLoadView(import('@views/pages/advertisers/edit')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/inventory/lists_edit/:id',
    component: () => lazyLoadView(import('@views/pages/inventory/edit')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  }

  

];


const adminRoutes = [
  {
    path: '/admin',
    name: 'Admin',
    icon: 'settings',
    meta: { authRequired: true },
    sidebar:true,
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'users',
        name: 'User Management',
        meta: { authRequired: true,onlyParent:true },
        component: () => import('@views/pages/users/list'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'advertisers',
        name: 'Advertisers',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/advertisers/list'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      }
    ],
  },
];


const reportRoutes = [
  {
    path: '/report',
    name: 'Reporting',
    icon: 'bar-chart',
    meta: { authRequired: true },
    sidebar:true,
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: 
    [

      {
        path: 'reports/analytics',
        name: 'Analytics Report',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/reports/analytics'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: '/reports/analytics/viewer/:id',
        component: () => import('@views/pages/reports/analytics/viewer'),
        meta: { authRequired: true },
        sidebar:false,
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'reports/performance',
        name: 'Performance Report',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/reports/performance'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'reports/domains',
        name: 'Domain Report',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/reports/domains'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'reports/apps',
        name: 'Apps Report',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/reports/apps'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'reports/geo',
        name: 'Geography Report',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/reports/geo'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      }

     
    ],
  },
];


const DSPRoutes = [
  {
    path: '/dsp',
    name: 'Audience',
    icon: 'users',
    meta: { authRequired: true },
    sidebar:true,
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'audience/farming',
        name: 'Data Farming',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/audience/farming/list'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'audience/marketplace',
        name: 'Marketplace',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/audience/marketplace'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'audience/geofencing',
        name: 'GEOfencing',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/audience/geofencing/list/index'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: '/audience/geofencing/edit/:id',
        component: () => lazyLoadView(import('@views/pages/audience/geofencing/edit/index')),
        meta: { authRequired: true },
        sidebar:false,
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: '/audience/farming/edit/:id',
        component: () => lazyLoadView(import('@views/pages/audience/farming/edit/index')),
        meta: { authRequired: true },
        sidebar:false,
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: '/audience/farming/view/:id',
        component: () => lazyLoadView(import('@views/pages/audience/farming/view/index')),
        meta: { authRequired: true },
        sidebar:false,
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: '/audience/farming/view_entity/:id',
        component: () => lazyLoadView(import('@views/pages/audience/farming/view_entity/index')),
        meta: { authRequired: true },
        sidebar:false,
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      }

      
    ],
  },
]



const InventoryRoutes = [
  {
    path: '/inventory',
    name: 'Inventory',
    icon: 'list',
    meta: { authRequired: true },
    sidebar:true,
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [

      {
        path: 'inventory/apps',
        name: 'App Lists',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/inventory/apps'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'inventory/websites',
        name: 'Website Lists',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/inventory/websites'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'inventory/lists',
        name: 'Optimization Lists',
        meta: { authRequired: true,onlyParent:false },
        component: () => import('@views/pages/inventory/lists'),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      }


      
    ],
  },
]


const campaignsRoutes = [
  {
    path: '/campaigns',
    name: 'Campaigns',
    icon: 'layers',
    sidebar:true,
    component: () => lazyLoadView(import('@views/pages/campaigns/list')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/campaigns/edit/:id',
    component: () => lazyLoadView(import('@views/pages/campaigns/edit')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/campaigns/overview/:id',
    component: () => lazyLoadView(import('@views/pages/campaigns/details')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/campaigns/:id/lineitems/:lid',
    component: () => lazyLoadView(import('@views/pages/campaigns/lineitems')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/campaigns/:id/youtube/:lid',
    component: () => lazyLoadView(import('@views/pages/campaigns/lineitems/youtube')),
    meta: { authRequired: true },
    sidebar:false,
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
];




const authProtectedRoutes = [
  ...dashboardRoutes,
  ...campaignsRoutes,
  ...settingsRoutes,
  ...hiddenRoutes,
  ...InventoryRoutes,
  ...DSPRoutes,
  ...creativesRoutes,
  ...reportRoutes,
  ...adminRoutes
]
const allRoutes = [...authRoutes, ...authProtectedRoutes, ...errorPagesRoutes]

export { allRoutes, authProtectedRoutes }

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@components/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
